import React, { useEffect, useImperativeHandle, useState } from 'react';
import {
  Form,
  Input,
  Button,
  Select,
  DatePicker,
  Radio,
  Checkbox,
  FormProps,
  TimePicker,
  FormItemProps,
  Switch,
  InputNumber,
  Cascader,
} from 'antd';
import moment from 'moment';
import { debounce, get } from 'lodash';
import { FormInstance } from 'antd/es/form/Form';
import { SearchOutlined } from '@ant-design/icons';
import intl from 'react-intl-universal';

import { filterFormParams, IdataSource, uuid } from '@/utils';
import { DATE_TIME_FORMAT, TIME_FORMAT } from '@/config';
import styles from './index.module.scss';

const { RangePicker } = DatePicker;
const { TextArea, Password } = Input;

type FieldType =
  | 'text'
  | 'password'
  | 'select'
  | 'datePicker'
  | 'dateRange'
  | 'dateTimeRange'
  | 'timePicker'
  | 'timeRange'
  | 'textArea'
  | 'radio'
  | 'radioButton'
  | 'checkbox'
  | 'search'
  | 'switch'
  | 'inputNumber'
  | 'group'
  | 'cascader';

export type RenderFunction = (values: FormInstance) => React.ReactElement;

export interface IFieldItem {
  label?: string;
  key?: string | Array<string>;
  type?: FieldType;
  placeholder?: string;
  editable?: boolean;
  rules?: Array<Object>;
  dataSource?: Array<IdataSource>;
  isDeleteAllBeforeDataSource?: boolean;
  fieldProps?: FormItemProps;
  style?: React.CSSProperties;
  leftExtra?: string | React.ReactNode;
  rightExtra?: string | React.ReactNode;
  // 表单组件props
  componentProps?: any;
  // 自定义组件
  component?: any;
  shouldUpdate?: (prevValue: any, curValue: any) => boolean;
}

export interface IMyFormRef extends FormInstance<any> {}

export interface IFormProps extends FormProps {
  submitType?: 'filter' | 'submit';
  layout?: 'horizontal' | 'vertical' | 'inline';
  columns?: number;
  labelCol?: Object;
  wrapperCol?: Object;
  btnLabelCol?: Object;
  btnWrapperCol?: Object;
  showBtn?: boolean;
  noReset?: boolean;
  noSearch?: boolean;
  okText?: string;
  queryText?: string;
  resetText?: string;
  isShowFilterBtn?: boolean;
  formAttr?: Array<IFieldItem>;
  advancedFilterCount?: number;
  value?: any;
  ref?: any;
  onFinish?: (values: any) => void;
  onValuesChange?: (changedValues: any, values: any) => void;
  onReset?: (values?: any) => void;
  onClickAdvancedFilter?: (..._) => void;
}

// const responsive = {
//   1: { xs: 24 },
//   2: { xs: 24, sm: 12 },
//   3: { xs: 24, sm: 12, md: 8 },
//   4: { xs: 24, sm: 12, md: 6 },
// };

const CustomForm: React.FC<IFormProps> = React.forwardRef<IMyFormRef, IFormProps>(
  (
    {
      submitType = 'submit',
      layout = 'horizontal',
      labelCol = { span: 6 },
      wrapperCol = { span: 24 },
      btnWrapperCol = { span: 6, offset: 6 },
      showBtn = false,
      noReset = false,
      noSearch = false,
      queryText = intl.get('app_search_form_search_btn'),
      isShowFilterBtn = false,
      formAttr = [],
      advancedFilterCount = 0,
      value,
      onFinish,
      onValuesChange,
      onReset,
      onClickAdvancedFilter,
      ...rest
    },
    ref,
  ) => {
    const [initValue, setInitValue] = useState({});
    const [form] = Form.useForm();
    const handleSearch = debounce((values) => {
      // console.log('submit--formParams', values);
      if (onFinish) {
        onFinish(submitType !== 'filter' ? values : filterFormParams(values));
      }
    }, 200);
    const handleValuesChange = (changedValues, allValues) => {
      const obj = {};
      Object.keys(changedValues)?.forEach((key) => {
        obj[key] =
          changedValues[key] !== null && changedValues[key] !== undefined ? changedValues[key] : '';
      });
      form.setFieldsValue({ ...obj });
      if (onValuesChange) {
        onValuesChange(changedValues, filterFormParams(allValues));
      }
    };
    const handleClickAdvancedFilter = () => {
      if (onClickAdvancedFilter) {
        onClickAdvancedFilter();
      }
    };
    const handleReset = debounce(() => {
      form.resetFields();
      if (onReset) {
        onReset();
      }
    }, 200);
    const getFieldItem = (item: IFieldItem, index: number): React.ReactNode => {
      const {
        key,
        type,
        dataSource = [],
        isDeleteAllBeforeDataSource,
        label,
        placeholder,
        editable = true,
        componentProps,
      } = item;
      const { mode } = componentProps || {};
      let field;
      let placeholderStr = `${intl.get('app_search_form_placeholder_prefix')}${label}`;
      if (placeholder) {
        placeholderStr = placeholder;
      }
      if (type === 'group') {
        return React.cloneElement(item.component, {
          key: item.key || `group-${index}`,
          ...componentProps,
        });
      }
      switch (type) {
        case 'text':
          field = <Input placeholder={placeholderStr} disabled={!editable} />;
          break;
        case 'password':
          field = <Password placeholder={placeholderStr} disabled={!editable} />;
          break;
        case 'textArea':
          field = <TextArea placeholder={placeholderStr} rows={4} disabled={!editable} />;
          break;
        case 'select': {
          const temp = [...dataSource];
          const val = get(value, key);
          if (
            submitType !== 'filter' &&
            mode !== 'multiple' &&
            (val === '' || val === undefined || val === null)
          ) {
            temp.unshift({ label: intl.get('app_search_form_option_label_please'), value: '' });
          }
          if (submitType === 'filter' && mode !== 'multiple' && !isDeleteAllBeforeDataSource) {
            temp.unshift({ label: intl.get('app_search_form_option_label_all'), value: '' });
          }
          field = <Select options={temp} disabled={!editable} />;
          break;
        }
        case 'datePicker':
          field = <DatePicker showTime placeholder={placeholderStr} disabled={!editable} />;
          break;
        case 'dateRange':
          field = (
            <RangePicker
              placeholder={[
                intl.get('app_search_form_placeholder_date_start'),
                intl.get('app_search_form_placeholder_date_end'),
              ]}
              disabled={!editable}
            />
          );
          break;
        case 'dateTimeRange':
          field = (
            <RangePicker
              showTime={{ format: TIME_FORMAT }}
              format={DATE_TIME_FORMAT}
              placeholder={[
                intl.get('app_search_form_placeholder_date_start'),
                intl.get('app_search_form_placeholder_date_end'),
              ]}
              disabled={!editable}
            />
          );
          break;
        case 'timePicker':
          field = <TimePicker disabled={!editable} />;
          break;
        case 'timeRange':
          field = <TimePicker.RangePicker disabled={!editable} />;
          break;

        case 'radio':
          const radioSource = [...dataSource];
          if (submitType === 'filter' && !isDeleteAllBeforeDataSource) {
            radioSource.unshift({ label: intl.get('app_search_form_option_label_all'), value: '' });
          }
          field = <Radio.Group options={radioSource} disabled={!editable} />;
          break;
        case 'radioButton': {
          const radioBtnSource = [...dataSource];
          if (submitType === 'filter' && !isDeleteAllBeforeDataSource) {
            radioBtnSource.unshift({
              label: intl.get('app_search_form_option_label_all'),
              value: '',
            });
          }
          field = <Radio.Group options={radioBtnSource} optionType="button" disabled={!editable} />;
          break;
        }
        case 'checkbox':
          field = <Checkbox.Group options={dataSource} disabled={!editable} />;
          break;
        case 'search':
          field = (
            <Input
              prefix={<SearchOutlined style={{ color: '#969799' }} />}
              allowClear
              placeholder={placeholderStr}
              disabled={!editable}
            />
          );
          break;
        case 'switch':
          field = <Switch disabled={!editable} />;
          break;
        case 'inputNumber':
          field = <InputNumber disabled={!editable} />;
          break;
        case 'cascader':
          field = <Cascader options={dataSource} disabled={!editable} />;
          break;
        default:
          field = <Input placeholder={placeholderStr} disabled={!editable} />;
      }
      return React.cloneElement(field, { ...componentProps });
    };
    const getFields = (): Array<React.ReactNode> => {
      if (Array.isArray(formAttr)) {
        return formAttr.map((item: IFieldItem, index: number) => {
          const {
            label,
            key,
            rules,
            type,
            editable = true,
            fieldProps,
            style,
            leftExtra,
            rightExtra,
          } = item;
          if ('shouldUpdate' in item) {
            return (
              <Form.Item noStyle key={`should-update-${uuid()}`} shouldUpdate={item.shouldUpdate}>
                {item.component}
              </Form.Item>
            );
          }
          return (
            <Form.Item
              style={style}
              key={key as string}
              label={label}
              name={key}
              rules={editable ? rules : []}
              valuePropName={type === 'switch' ? 'checked' : 'value'}
              {...fieldProps}
            >
              {leftExtra || rightExtra ? (
                <>
                  <div className={styles.extraContainer}>
                    {leftExtra && <div className={styles.left}>{leftExtra}</div>}
                    <div>
                      <Form.Item noStyle key={key as string} name={key}>
                        {getFieldItem(item, index)}
                      </Form.Item>
                    </div>
                    {rightExtra && <div className={styles.right}>{rightExtra}</div>}
                  </div>
                </>
              ) : (
                getFieldItem(item, index)
              )}
            </Form.Item>
          );
        });
      }
      return [];
    };

    const isRangeDate = (type) => {
      if (type === 'dateRange' || type === 'dateTimeRange' || type === 'timeRange') {
        return true;
      }
      return false;
    };
    const isDate = (type) => {
      if (type === 'datePicker' || type === 'timePicker') {
        return true;
      }
      return false;
    };
    // moment实例化
    const transformDate = (type, val) => {
      if (isRangeDate(type)) {
        return val?.[0] && val?.[1] ? [moment(val?.[0]), moment(val?.[1])] : [];
      }
      if (isDate(type)) {
        return val ? moment(val) : '';
      }
      return val;
    };
    const isMutilFn = (type, mode) => {
      if (type === 'checkbox' || (type === 'select' && mode === 'multiple') || isRangeDate(type)) {
        return true;
      }
      return false;
    };
    const transformMutilEmpty = (type, mode, val) => {
      if (val === '' || val === undefined || val === null) {
        const isMutil = isMutilFn(type, mode);
        return isMutil ? [] : '';
      }
      return val;
    };

    // 多选空值及日期值类型处理
    const transformMutileValue = (item, val) => {
      const { type, componentProps } = item;
      const { mode } = componentProps || {};

      let result = transformMutilEmpty(type, mode, val);

      if (isRangeDate(type) || isDate(type)) {
        result = transformDate(type, val);
      }
      return result;
    };

    useImperativeHandle(
      ref,
      () => ({
        ...form,
        setFieldsValue: (values) => {
          const initialValue = {};
          formAttr.forEach((item) => {
            const { key } = item;
            if (key) {
              if ('shouldUpdate' in item && Array.isArray(key)) {
                key?.forEach((subK) => {
                  const val = get(values, subK);
                  initialValue[subK] = transformMutileValue(item, val);
                });
              } else {
                const val = get(values, key);
                initialValue[key as string] = transformMutileValue(item, val);
              }
            }
          });
          form.setFieldsValue(initialValue);
        },
      }),
      [formAttr],
    );

    useEffect(() => {
      if (Array.isArray(formAttr) && formAttr.length) {
        const initialValue = {};
        formAttr.forEach((item) => {
          const { key } = item;
          if (key) {
            if ('shouldUpdate' in item && Array.isArray(key)) {
              key?.forEach((subK) => {
                const val = get(value, subK);
                initialValue[subK] = transformMutileValue(item, val);
              });
            } else {
              const val = get(value, key);
              initialValue[key as string] = transformMutileValue(item, val);
            }
          }
        });
        // console.log('The form initializes the value---', initialValue);
        setInitValue(initialValue);
        form.setFieldsValue({ ...initialValue });
      }
    }, [formAttr, value]);

    return (
      <>
        <div className={styles['wrapper-form']}>
          <Form
            initialValues={initValue}
            name={uuid()}
            preserve={false}
            form={form}
            layout={layout}
            labelCol={layout !== 'inline' ? labelCol : {}}
            wrapperCol={layout !== 'inline' ? wrapperCol : {}}
            onFinish={handleSearch}
            onValuesChange={handleValuesChange}
            {...rest}
          >
            {getFields()}
            {submitType !== 'filter' && showBtn && (
              <Form.Item wrapperCol={btnWrapperCol}>
                <Button type="primary" htmlType="submit">
                  {intl.get('app_search_form_save_btn')}
                </Button>
              </Form.Item>
            )}
            {submitType === 'filter' && showBtn && (
              <>
                <div style={{ display: 'flex' }}>
                  {!noSearch && (
                    <Form.Item className={styles.searchBtn}>
                      <Button type="primary" htmlType="submit">
                        {queryText}
                      </Button>
                    </Form.Item>
                  )}
                  {isShowFilterBtn && (
                    <div
                      className={`${styles.searchBtn} ${styles.advancedFilter} ${
                        advancedFilterCount ? styles.active : ''
                      }`}
                      onClick={handleClickAdvancedFilter}
                    >
                      <div>{intl.get('app_search_form_advanced_filter_btn')}</div>
                      {advancedFilterCount ? (
                        <div className={styles.filterCount}>{advancedFilterCount}</div>
                      ) : null}
                    </div>
                  )}
                  {!noReset && (
                    <Form.Item className={styles.searchBtn}>
                      <Button type="ghost" onClick={handleReset}>
                        {intl.get('app_search_form_reset_btn')}
                      </Button>
                    </Form.Item>
                  )}
                </div>
              </>
            )}
          </Form>
        </div>
      </>
    );
  },
);

export default React.memo(CustomForm);
